import {
  DEFAULT_COMPANY_TIME_ZONE,
  IMMEDIATE_START,
  LEGAL_DOCUMENTS,
  DISTRICTS,
  districtOf,
  PROVINCES,
  type Province,
  formatPlanTokens,
  formatTry,
  splitVat,
  validNationalId,
  validTaxNumber,
  zonedParts,
  type BillingPeriod,
  type CheckoutStartDto,
  type CheckoutStatusDto,
  type PlanDto,
} from '@dbrain/shared';
import { Alert, Button, Field, Icons, OppyWordmark, Select, TextInput } from '@dbrain/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useBillingOverview, useBillingRefresh, usePayments } from '../../api/billing.js';
import { api } from '../../api/client.js';
import { keys } from '../../api/hooks.js';
import { useProPlan } from '../../api/plan.js';
import { HostedFormDialog } from '../../components/billing/HostedFormDialog.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';

/**
 * Phase 16c (B2cOdeme): "Pro'ya geçin": the period, the billing details, the order summary with its VAT, the two texts
 * to accept (their versions are sent and recorded), then the provider's form in a dialog (B2cOdemeKart). A payment
 * taken opens Pro at once and the home page greets it (B2cOdemeTamam). Not where payments are closed: the note says so.
 */
export interface ProfileForm {
  fullName: string;
  nationalId: string;
  province: string;
  district: string;
  address: string;
  company: boolean;
  companyName: string;
  taxNumber: string;
  taxOffice: string;
}

export const emptyProfile: ProfileForm = { fullName: '', nationalId: '', province: 'İstanbul', district: '', address: '', company: false, companyName: '', taxNumber: '', taxOffice: '' };

/** The fields that are not right yet, by name (the page shows them under each field) */
export function profileProblems(form: ProfileForm): Partial<Record<keyof ProfileForm, string>> {
  const problems: Partial<Record<keyof ProfileForm, string>> = {};
  if (form.fullName.trim().length < 2) problems.fullName = 'required';
  // optional: only a number that is there must be a valid one
  if (form.nationalId.trim() && !validNationalId(form.nationalId.trim())) problems.nationalId = 'nationalId';
  if (!(PROVINCES as readonly string[]).includes(form.province)) problems.province = 'required';
  if (!districtOf(form.province, form.district)) problems.district = 'required';
  if (form.address.trim().length < 5) problems.address = 'address';
  if (form.company) {
    if (form.companyName.trim().length < 2) problems.companyName = 'required';
    if (!validTaxNumber(form.taxNumber.trim())) problems.taxNumber = 'taxNumber';
    if (form.taxOffice.trim().length < 2) problems.taxOffice = 'required';
  }
  return problems;
}

/** The body the API takes; the national id is left out when the field still shows the kept one's hint */
export function profileBody(form: ProfileForm, nationalIdChanged: boolean) {
  return {
    fullName: form.fullName.trim(),
    ...(nationalIdChanged ? { nationalId: form.nationalId.trim() || null } : {}),
    province: form.province,
    district: form.district.trim(),
    address: form.address.trim(),
    company: form.company,
    ...(form.company ? { companyName: form.companyName.trim(), taxNumber: form.taxNumber.trim(), taxOffice: form.taxOffice.trim() } : {}),
  };
}

/** "2 ay bedava": the months a year's price saves against twelve months */
export function freeMonths(plan: Pick<PlanDto, 'monthlyPriceKurus' | 'yearlyPriceKurus'>): number {
  if (!plan.yearlyPriceKurus || plan.monthlyPriceKurus <= 0) return 0;
  return Math.max(0, Math.round(12 - plan.yearlyPriceKurus / plan.monthlyPriceKurus));
}

export function ProfileFields({ form, onChange, problems, nationalIdHint, show }: { form: ProfileForm; onChange: (next: Partial<ProfileForm>) => void; problems: Partial<Record<keyof ProfileForm, string>>; nationalIdHint: string | null; show: boolean }) {
  const { t } = useTranslation();
  const error = (field: keyof ProfileForm) => (show && problems[field] ? t(`b2cBilling.checkout.problems.${problems[field]}`) : undefined);
  return (
    <div className="checkout-fields">
      <Field label={t('b2cBilling.checkout.fields.fullName')} error={error('fullName')}>
        {(f) => (
          <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={form.fullName} autoComplete="name" onChange={(e) => onChange({ fullName: e.target.value })} data-testid="billing-name" />
        )}
      </Field>
      <Field label={<>{t('b2cBilling.checkout.fields.nationalId')} <span className="muted">{t('b2cBilling.checkout.optional')}</span></>} hint={t('b2cBilling.checkout.fields.nationalIdHint')} error={error('nationalId')}>
        {(f) => (
          <TextInput
            id={f.id}
            aria-describedby={f.describedBy}
            aria-invalid={f.invalid || undefined}
            value={form.nationalId}
            inputMode="numeric"
            maxLength={11}
            placeholder={nationalIdHint ? t('b2cBilling.checkout.fields.nationalIdKept', { hint: nationalIdHint }) : t('b2cBilling.checkout.fields.nationalIdPlaceholder')}
            onChange={(e) => onChange({ nationalId: e.target.value.replace(/\D/g, '') })}
            data-testid="billing-national-id"
          />
        )}
      </Field>
      <Field label={t('b2cBilling.checkout.fields.province')} error={error('province')}>
        {(f) => (
          <Select
            id={f.id}
            aria-describedby={f.describedBy}
            aria-invalid={f.invalid || undefined}
            value={form.province}
            // 16c.1: another İl's İlçe list; the İlçe chosen before goes when it is not in it
            onChange={(e) => onChange({ province: e.target.value, ...(districtOf(e.target.value, form.district) ? {} : { district: '' }) })}
            data-testid="billing-province"
          >
            {PROVINCES.map((province) => (
              <option key={province} value={province}>
                {province}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {/* 16c.1 (B2cOdeme): the İlçe is a list of the chosen İl's districts */}
      <Field label={t('b2cBilling.checkout.fields.district')} error={error('district')}>
        {(f) => (
          <Select
            id={f.id}
            aria-describedby={f.describedBy}
            aria-invalid={f.invalid || undefined}
            value={districtOf(form.province, form.district) ? form.district : ''}
            autoComplete="address-level2"
            onChange={(e) => onChange({ district: e.target.value })}
            data-testid="billing-district"
          >
            <option value="">{t('b2cBilling.checkout.fields.districtChoose')}</option>
            {(DISTRICTS[form.province as Province] ?? []).map((district) => (
              <option key={district} value={district}>
                {district}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <div className="checkout-fields__wide">
        <Field label={t('b2cBilling.checkout.fields.address')} error={error('address')}>
          {(f) => (
            <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={form.address} autoComplete="street-address" onChange={(e) => onChange({ address: e.target.value })} data-testid="billing-address" />
          )}
        </Field>
      </div>
      {form.company ? (
        <>
          <div className="checkout-fields__wide">
            <Field label={t('b2cBilling.checkout.fields.companyName')} error={error('companyName')}>
              {(f) => (
                <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={form.companyName} autoComplete="organization" onChange={(e) => onChange({ companyName: e.target.value })} data-testid="billing-company-name" />
              )}
            </Field>
          </div>
          <Field label={t('b2cBilling.checkout.fields.taxNumber')} error={error('taxNumber')}>
            {(f) => (
              <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={form.taxNumber} inputMode="numeric" maxLength={10} onChange={(e) => onChange({ taxNumber: e.target.value.replace(/\D/g, '') })} data-testid="billing-tax-number" />
            )}
          </Field>
          <Field label={t('b2cBilling.checkout.fields.taxOffice')} error={error('taxOffice')}>
            {(f) => (
              <TextInput id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={form.taxOffice} onChange={(e) => onChange({ taxOffice: e.target.value })} data-testid="billing-tax-office" />
            )}
          </Field>
        </>
      ) : null}
      <button type="button" className="checkout-fields__company" onClick={() => onChange({ company: !form.company })} aria-expanded={form.company} data-testid="billing-company">
        {form.company ? t('b2cBilling.checkout.companyOff') : t('b2cBilling.checkout.companyOn')}
      </button>
    </div>
  );
}

/** "2’sinde", "9’unda", "30’unda": a day of the month with its Turkish locative, by how the number is read */
export function trDayLocative(day: number): string {
  const units: Record<number, string> = { 1: 'inde', 2: 'sinde', 3: 'ünde', 4: 'ünde', 5: 'inde', 6: 'sında', 7: 'sinde', 8: 'inde', 9: 'unda' };
  const tens: Record<number, string> = { 10: 'unda', 20: 'sinde', 30: 'unda' };
  return `${day}’${day % 10 === 0 ? tens[day] : units[day % 10]}`;
}

const renewalDay = (period: BillingPeriod, locale: string) => {
  const now = new Date();
  const p = zonedParts(now, DEFAULT_COMPANY_TIME_ZONE);
  if (period === 'monthly') return locale.startsWith('tr') ? trDayLocative(p.day) : String(p.day);
  return now.toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: DEFAULT_COMPANY_TIME_ZONE });
};

export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const payments = usePayments();
  const pro = useProPlan();
  const overview = useBillingOverview();
  const refresh = useBillingRefresh();
  const queryClient = useQueryClient();
  const period: BillingPeriod = params.get('period') === 'yearly' ? 'yearly' : 'monthly';
  const [form, setForm] = useState<ProfileForm>(emptyProfile);
  const [loaded, setLoaded] = useState(false);
  const [nationalIdChanged, setNationalIdChanged] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [immediate, setImmediate] = useState(false);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [failure, setFailure] = useState<CheckoutStatusDto['failure']>(null);
  const [checkout, setCheckout] = useState<CheckoutStartDto | null>(null);
  // paid: the home page greets it (B2cOdemeTamam); before the subscription's own redirect to Abonelik below
  const [paid, setPaid] = useState<CheckoutStatusDto | null>(null);
  const profile = overview.data?.profile ?? null;

  useEffect(() => {
    if (loaded || !overview.data) return;
    const p = overview.data.profile;
    if (p) {
      setForm({
        fullName: p.fullName,
        nationalId: '',
        province: p.province,
        district: p.district,
        address: p.address,
        company: p.company,
        companyName: p.companyName ?? '',
        taxNumber: p.taxNumber ?? '',
        taxOffice: p.taxOffice ?? '',
      });
    }
    setLoaded(true);
  }, [loaded, overview.data]);

  const problems = useMemo(() => profileProblems(form), [form]);
  if (paid) return <Navigate to="/" replace state={{ proWelcome: paid }} />;
  if (overview.data?.subscription) return <Navigate to="/settings/subscription" replace />;
  const locale = i18n.language;
  const price = pro ? (period === 'yearly' ? (pro.yearlyPriceKurus ?? 0) : pro.monthlyPriceKurus) : 0;
  const { net, vat } = splitVat(price);
  const months = pro ? freeMonths(pro) : 0;
  const description = t(`b2cBilling.periods.product.${period}`);
  const ready = Object.keys(problems).length === 0 && accepted && immediate;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    setFailure(null);
    if (!ready || !pro) return;
    setBusy(true);
    setError(null);
    try {
      const started = await api<CheckoutStartDto>('POST', '/api/billing/checkout', {
        period,
        billing: profileBody(form, nationalIdChanged),
        accept: { preInformation: LEGAL_DOCUMENTS.preInformation.version, distanceSales: LEGAL_DOCUMENTS.distanceSales.version, immediateStart: true },
      });
      setCheckout(started);
    } catch (failed) {
      setError(failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="checkout" data-testid="checkout-page">
      <header className="checkout__bar">
        <div className="checkout__brand">
          <OppyWordmark className="checkout__wordmark" />
          <span className="checkout__divider" aria-hidden="true" />
          <span className="checkout__where">{t('b2cBilling.checkout.where')}</span>
        </div>
        <Link to="/" className="ui-button ui-button--secondary ui-button--sm checkout__back" data-testid="checkout-back">
          <Icons.chevronLeft size={16} />
          {t('b2cBilling.checkout.back')}
        </Link>
      </header>
      {!payments.open ? (
        <main id="main" className="checkout__closed">
          <Alert tone="info" title={t('b2cBilling.checkout.closedTitle')} data-testid="checkout-closed">
            {t('b2cBilling.checkout.closedBody')}
          </Alert>
        </main>
      ) : (
        <form className="checkout__main" onSubmit={(e) => void submit(e)} noValidate>
          <section className="checkout__left" aria-labelledby="checkout-title">
            <h1 id="checkout-title" className="checkout__title">
              {t('b2cBilling.checkout.title')}
            </h1>
            <p className="checkout__lead">{t('b2cBilling.checkout.lead')}</p>
            <h2 className="checkout__step">
              <span className="checkout__step-number">1</span>
              {t('b2cBilling.checkout.periodTitle')}
            </h2>
            <div className="checkout__periods" role="radiogroup" aria-label={t('b2cBilling.checkout.periodTitle')}>
              {(['monthly', 'yearly'] as const).map((option) => {
                const amount = pro ? (option === 'yearly' ? (pro.yearlyPriceKurus ?? 0) : pro.monthlyPriceKurus) : 0;
                return (
                  <label key={option} className={`checkout-period${period === option ? ' checkout-period--on' : ''}`} data-testid={`checkout-period-${option}`}>
                    <input type="radio" name="period" className="ui-radio" checked={period === option} onChange={() => setParams({ period: option }, { replace: true })} />
                    <span className="checkout-period__text">
                      <span className="checkout-period__name">
                        {t(`b2cBilling.periods.${option}`)}
                        {option === 'yearly' && months > 0 ? <span className="ui-badge ui-badge--success">{t('b2cBilling.checkout.freeMonths', { count: months })}</span> : null}
                      </span>
                      <span className="checkout-period__price">{t(`b2cBilling.checkout.price.${option}`, { price: formatTry(amount, locale, true) })}</span>
                      <span className="checkout-period__hint">
                        {option === 'monthly' ? t('b2cBilling.checkout.monthlyHint') : t('b2cBilling.checkout.yearlyHint', { price: formatTry(Math.round(amount / 12), locale) })}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <h2 className="checkout__step">
              <span className="checkout__step-number">2</span>
              {t('b2cBilling.checkout.detailsTitle')}
            </h2>
            <ProfileFields
              form={form}
              onChange={(next) => {
                if ('nationalId' in next) setNationalIdChanged(true);
                setForm((current) => ({ ...current, ...next }));
              }}
              problems={problems}
              nationalIdHint={nationalIdChanged ? null : (profile?.nationalIdHint ?? null)}
              show={tried}
            />
          </section>
          <aside className="checkout__summary ui-card" aria-labelledby="checkout-summary">
            <h2 id="checkout-summary" className="checkout__summary-title">
              {t('b2cBilling.checkout.summary')}
            </h2>
            <div className="checkout__product">
              <span className="checkout__product-icon" aria-hidden="true">
                <Icons.skills size={18} />
              </span>
              <span>
                <span className="checkout__product-name">{description}</span>
                {pro ? (
                  <span className="checkout__product-limits">
                    {t('b2cBilling.checkout.limits', { tokens: formatPlanTokens(pro.dailyTokens, locale), documents: pro.maxDocuments.toLocaleString(locale), mb: pro.maxFileMb })}
                  </span>
                ) : null}
              </span>
            </div>
            <dl className="checkout__amounts">
              <div>
                <dt>{t('b2cBilling.checkout.net')}</dt>
                <dd data-testid="checkout-net">{formatTry(net, locale)}</dd>
              </div>
              <div>
                <dt>{t('b2cBilling.checkout.vat')}</dt>
                <dd data-testid="checkout-vat">{formatTry(vat, locale)}</dd>
              </div>
              <div className="checkout__total">
                <dt>{t('b2cBilling.checkout.today')}</dt>
                <dd data-testid="checkout-total">{formatTry(price, locale)}</dd>
              </div>
            </dl>
            <p className="checkout__renewal">{t(`b2cBilling.checkout.renewal.${period}`, { day: renewalDay(period, locale), price: formatTry(price, locale) })}</p>
            <label className="checkout__accept">
              <input type="checkbox" className="ui-checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="checkout-accept" />
              <span>
                <a href={`/yasal/${LEGAL_DOCUMENTS.preInformation.slug}`} target="_blank" rel="noopener">
                  {LEGAL_DOCUMENTS.preInformation.title}
                </a>
                {t('b2cBilling.checkout.acceptJoin')}
                <a href={`/yasal/${LEGAL_DOCUMENTS.distanceSales.slug}`} target="_blank" rel="noopener">
                  {LEGAL_DOCUMENTS.distanceSales.title}
                </a>
                {t('b2cBilling.checkout.acceptEnd')}
              </span>
            </label>
            <label className="checkout__accept">
              <input type="checkbox" className="ui-checkbox" checked={immediate} onChange={(e) => setImmediate(e.target.checked)} data-testid="checkout-immediate" />
              <span>{IMMEDIATE_START.text}</span>
            </label>
            {tried && (!accepted || !immediate) ? (
              <p className="checkout__problem" role="alert">
                {t('b2cBilling.checkout.problems.accept')}
              </p>
            ) : null}
            {tried && Object.keys(problems).length > 0 ? (
              <p className="checkout__problem" role="alert">
                {t('b2cBilling.checkout.problems.fields')}
              </p>
            ) : null}
            {failure ? (
              <Alert tone="error" title={t('b2cBilling.checkout.failedTitle')} data-testid="checkout-failed">
                {failure.message ? `${failure.message} ` : ''}
                {t('b2cBilling.checkout.failedBody')}
              </Alert>
            ) : null}
            <ErrorAlert error={error} />
            <Button type="submit" block size="lg" busy={busy} disabled={!pro} data-testid="checkout-pay">
              {t('b2cBilling.checkout.pay', { price: formatTry(price, locale) })}
            </Button>
            <p className="checkout__lock">
              <Icons.lock size={14} aria-hidden="true" />
              {t(payments.test ? 'b2cBilling.checkout.lockTest' : 'b2cBilling.checkout.lock')}
            </p>
          </aside>
        </form>
      )}
      {checkout ? (
        <HostedFormDialog
          checkout={checkout}
          description={description}
          onClose={() => setCheckout(null)}
          onFailed={(status) => {
            setCheckout(null);
            setFailure(status.failure ?? { code: status.status, message: '' });
          }}
          onPaid={(status) => {
            setCheckout(null);
            // the header and the sidebar say Pro before the greeting shows
            void queryClient
              .refetchQueries({ queryKey: keys.me })
              .catch(() => undefined)
              .finally(() => {
                setPaid(status);
                refresh(status.subscription);
              });
          }}
        />
      ) : null}
    </div>
  );
}
